// src/router/index.js
import { createRouter, createWebHistory } from "vue-router";
import { appRoutes } from "./routes";
import { hasMinimumLevel } from "@/constants/adminLevels";
import { logger } from "@/utils/logger";
import { recoverFromChunkLoadError } from "@/utils/chunkLoadRecovery";

import PublicLayout from "@/layouts/PublicLayout.vue";
import MainLayout from "@/layouts/MainLayout.vue";
import LoginPage from "@/components/Login.vue";
import RegisterPage from "@/views/Register.vue";
import ForbiddenAccess from "@/views/ForbiddenAccess.vue";
import ChangePassword from "@/views/ChangePassword.vue";

import { useAuthStore } from "@/stores/auth";

// PRD-002 RF-17 — flatten recursivo: routes com `children` viram nested vue-router
// children. Para a sidebar (em MainLayout) o `children` do appRoutes original é
// tratado separadamente — aqui é só pra registro do roteador.
function flattenRouteForRouter(route, parentPath = "") {
  // Filho com path ABSOLUTO (começa com "/") não herda o prefixo do pai — assim
  // um grupo PURAMENTE de menu (Operação/Gestão/Agenda) agrupa visualmente sem
  // mudar a URL real (/patients continua /patients, não /gestao/patients).
  // Filho com path relativo ("pipeline") segue aninhando sob o pai como antes.
  const isAbsolute = route.path.startsWith("/");
  const fullPath = isAbsolute
    ? route.path.replace(/^\/+/, "").replace(/\/+/g, "/")
    : (parentPath + "/" + route.path)
        .replace(/\/+/g, "/")
        .replace(/^\/+/, "");
  const meta = {
    requiresAuth: true,
    minLevel: route.minLevel,
  };
  if (route.component) {
    return [{
      path: fullPath,
      name: route.name,
      component: route.component,
      meta,
    }];
  }
  // Group node sem componente — registra como redirect e expande children.
  const out = [];
  if (route.redirect) {
    out.push({
      path: fullPath,
      name: route.name,
      redirect: route.redirect,
      meta,
    });
  }
  for (const child of route.children || []) {
    out.push(...flattenRouteForRouter(child, "/" + fullPath));
  }
  return out;
}

const privateRoutes = {
  path: "/",
  component: MainLayout,
  meta: { requiresAuth: true },
  children: appRoutes.flatMap((route) => flattenRouteForRouter(route)),
};

const publicRoutes = {
  path: "/",
  component: PublicLayout,
  children: [
    {
      path: "",
      name: "LoginPage",
      component: LoginPage,
    },
    {
      path: "register",
      name: "RegisterPage",
      component: RegisterPage,
    },
    {
      path: "forbidden",
      name: "ForbiddenAccess",
      component: ForbiddenAccess,
    },
    {
      path: "change-password",
      name: "ChangePassword",
      component: ChangePassword,
      meta: { requiresAuth: true, skipPasswordCheck: true },
    },
  ],
};

const routes = [
  publicRoutes,
  privateRoutes,
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// 🟢 Usa Pinia no beforeEach com verificação de Admin Level e Troca de Senha Obrigatória
router.beforeEach((to, from, next) => {
  const auth = useAuthStore();

  // 1. Rota protegida, mas não logado
  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    logger.router("Usuário não logado, redirecionando para login");
    return next({ name: "LoginPage" });
  }

  // 2. Está logado e tenta acessar login
  if (to.name === "LoginPage" && auth.isLoggedIn) {
    // Se precisa trocar senha, redireciona para troca
    if (auth.needsPasswordChange) {
      logger.router("Usuário precisa trocar senha, redirecionando");
      return next({ name: "ChangePassword" });
    }
    logger.router("Usuário já logado, redirecionando para home");
    return next({ path: "/home" });
  }

  // 3. Verifica se precisa trocar senha (exceto na própria página de troca)
  if (
    auth.isLoggedIn &&
    auth.needsPasswordChange &&
    !to.meta.skipPasswordCheck
  ) {
    logger.router("Troca de senha obrigatória, redirecionando");
    return next({ name: "ChangePassword" });
  }

  // 4. Verifica permissão baseada em Admin Level
  if (to.meta.minLevel !== undefined) {
    const userLevel = auth.adminLevelValue;
    const requiredLevel = to.meta.minLevel;
    const isAllowed = hasMinimumLevel(userLevel, requiredLevel);

    logger.debug(`Verificando permissão para ${to.name}`, {
      userLevel,
      requiredLevel,
      isAllowed,
      adminLevelName: auth.adminLevelDisplayName,
    });

    if (!isAllowed) {
      logger.security(
        `Acesso negado para ${to.name} - Nível ${userLevel} < ${requiredLevel}`,
      );
      return next({
        name: "ForbiddenAccess",
        query: {
          from: to.fullPath,
          required: requiredLevel,
        },
      });
    }
  }

  // 5. Libera
  logger.debug(`Acesso permitido para ${to.name}`);
  next();
});

// BUG-051 — aba aberta antes de um deploy pede um pedaço de tela que não existe
// mais na build nova. Sem isto o clique no menu não faz nada até Ctrl+Shift+R.
router.onError((error, to) => {
  if (!recoverFromChunkLoadError(error, to?.fullPath)) {
    logger.error("[router] falha na navegação", error);
  }
});

export default router;
